const INTERSTITIAL_COOLDOWN_MS = 12000;
const SCROLL_AD_TRIGGER_PX = 20;
const LOADER_MAX_MS = 3000;
const AD_READY_MAX_WAIT_MS = 3000;
const AD_START_TIMEOUT_MS = 8000;
const SESSION_WELCOME_KEY = 'welcome_ad_shown';
const SESSION_SCROLL_KEY = 'scroll_ad_shown';

let interstitialInProgress = false;
let lastInterstitialTime = 0;
let rewardGranted = false;
let loaderTimer = null;
let adLoadingVisible = false;
const adLoadingListeners = new Set();
let adQueue = Promise.resolve();

const notifyLoading = (visible) => {
  adLoadingVisible = visible;
  adLoadingListeners.forEach((listener) => listener(visible));
};

export const subscribeAdLoading = (listener) => {
  adLoadingListeners.add(listener);
  listener(adLoadingVisible);
  return () => adLoadingListeners.delete(listener);
};

export const showAdLoading = () => {
  notifyLoading(true);
  if (loaderTimer) clearTimeout(loaderTimer);
  loaderTimer = setTimeout(hideAdLoading, LOADER_MAX_MS);
};

export const hideAdLoading = () => {
  if (loaderTimer) {
    clearTimeout(loaderTimer);
    loaderTimer = null;
  }
  notifyLoading(false);
};

export function initializeAds() {
  return new Promise((resolve) => {
    if (window.afg?.ready) {
      resolve(true);
      return;
    }

    let resolved = false;
    const done = (value) => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };

    window.addEventListener('ads:ready', () => done(true), { once: true });

    const started = Date.now();
    const poll = () => {
      if (window.afg?.ready) {
        done(true);
        return;
      }
      if (Date.now() - started >= AD_READY_MAX_WAIT_MS) {
        done(false);
        return;
      }
      setTimeout(poll, 100);
    };
    poll();
  });
}

const runQueued = (task) => {
  adQueue = adQueue.then(task).catch(() => {});
  return adQueue;
};

const waitUntilAdIdle = () =>
  new Promise((resolve) => {
    const check = () => {
      if (!interstitialInProgress) {
        resolve();
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });

const showInterstitialAdInternal = ({
  name = 'navigation',
  type = 'preroll',
  force = false,
} = {}) =>
  new Promise((resolve) => {
    let adStarted = false;

    const finish = (result = { started: adStarted }) => {
      interstitialInProgress = false;
      hideAdLoading();
      resolve(result);
    };

    const canShow =
      force || Date.now() - lastInterstitialTime >= INTERSTITIAL_COOLDOWN_MS;

    if (!canShow) {
      finish();
      return;
    }

    showAdLoading();

    initializeAds().then((isReady) => {
      if (!isReady || typeof window.adBreak !== 'function') {
        finish();
        return;
      }

      interstitialInProgress = true;
      let finished = false;

      const safeFinish = () => {
        if (finished) return;
        finished = true;
        lastInterstitialTime = Date.now();
        finish({ started: adStarted });
      };

      const startTimeout = setTimeout(() => {
        if (!adStarted) safeFinish();
      }, AD_START_TIMEOUT_MS);

      try {
        window.adBreak({
          type,
          name,
          beforeAd: () => {
            adStarted = true;
            clearTimeout(startTimeout);
            hideAdLoading();
          },
          adBreakDone: () => {
            clearTimeout(startTimeout);
            safeFinish();
          },
        });
      } catch {
        clearTimeout(startTimeout);
        safeFinish();
      }
    });
  });

export function showInterstitialAd(options = {}) {
  return runQueued(async () => {
    await waitUntilAdIdle();
    return showInterstitialAdInternal(options);
  });
}

export function navigateWithInterstitial(url) {
  return showInterstitialAd({
    type: 'preroll',
    name: 'navigation',
    force: true,
  }).then(() => {
    window.location.href = url;
  });
}

const playPrerollForReward = () =>
  showInterstitialAd({
    type: 'preroll',
    name: 'reward-fallback',
    force: true,
  }).then(({ started }) => {
    if (!rewardGranted && started) {
      rewardGranted = true;
      if (window.onAdRewarded) window.onAdRewarded();
      window.location.reload();
    }
  });

export function loadRewardAd() {
  rewardGranted = false;
  showAdLoading();

  initializeAds().then((isReady) => {
    if (!isReady || typeof window.adBreak !== 'function') {
      playPrerollForReward();
      return;
    }

    try {
      window.adBreak({
        type: 'reward',
        name: 'rewarded-video',
        beforeReward: (showAdFn) => showAdFn(),
        beforeAd: () => {
          hideAdLoading();
          if (window.onAdStarted) window.onAdStarted();
        },
        adViewed: () => {
          rewardGranted = true;
          if (window.onAdRewarded) window.onAdRewarded();
          window.location.reload();
        },
        adDismissed: () => {},
        adBreakDone: () => {
          hideAdLoading();
          if (!rewardGranted) playPrerollForReward();
        },
      });
    } catch {
      playPrerollForReward();
    }
  });
}

export function initWelcomeAd() {
  if (sessionStorage.getItem(SESSION_WELCOME_KEY)) return;
  sessionStorage.setItem(SESSION_WELCOME_KEY, '1');

  setTimeout(() => {
    showInterstitialAd({ type: 'preroll', name: 'welcome', force: true });
  }, 500);
}

export function initScrollInterstitial(scrollElement) {
  if (!scrollElement || sessionStorage.getItem(SESSION_SCROLL_KEY)) return;

  const onScroll = () => {
    if (scrollElement.scrollTop < SCROLL_AD_TRIGGER_PX) return;
    scrollElement.removeEventListener('scroll', onScroll);
    sessionStorage.setItem(SESSION_SCROLL_KEY, '1');
    showInterstitialAd({ type: 'interstitial', name: 'scroll', force: true });
  };

  scrollElement.addEventListener('scroll', onScroll, { passive: true });
}

window.loadRewardAd = loadRewardAd;
window.navigateWithInterstitial = navigateWithInterstitial;
window.showInterstitialAd = showInterstitialAd;
